import { LoadingOutlined } from "@ant-design/icons";
import { Input, Modal, Spin, Upload } from "antd";
import { useState } from "react";
import ImgCrop from "antd-img-crop";
import { AiFillEdit } from "react-icons/ai";
import { HiSave } from "react-icons/hi";
import { MdDelete } from "react-icons/md";
import apiFactory from "../../api";
import { useInfoUser } from "../../store/UserStore";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../utils/Utils";
import "./style.scss";
import { CiCamera } from "react-icons/ci";
import { GeneralModal } from "./GeneralModal";
import { uploadMultipartFile } from "../../api/authentication/storageApi";
import { ProfileAvatar } from "../avatar/ProfileAvatar";

const ProfileModal = ({ isModalOpen, closeModal }) => {
  const {
    user,
    languageMap,
    updateUser,
    updateLanguageMap,
    updateStickerKeywords,
  } = useInfoUser();
  const [isEditMood, setIsEditMood] = useState(false);
  const [isEditPhone, setIsEditPhone] = useState(false);
  const [isEditPosition, setIsEditPosition] = useState(false);
  const [profile, setProfile] = useState(user);
  const [isLoading, setIsLoading] = useState(false);
  const [isRemoveAvatar, setIsRemoveAvatar] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState(null);

  const onChangeProfile = (e, field) => {
    setProfile({
      ...profile,
      mood: field === "mood" ? e?.target?.value : profile?.mood,
      phone: field === "phone" ? e?.target?.value : profile?.phone,
      position: field === "position" ? e?.target?.value : profile?.position,
    });
  };

  const onStoreProfile = async (field) => {
    switch (field) {
      case "mood":
        setIsEditMood(false);
        break;
      case "phone":
        setIsEditPhone(false);
        break;
      case "position":
        setIsEditPosition(false);
        break;
      default:
        break;
    }

    const params = {
      language: user?.language,
      phone: profile?.phone,
      mood: profile?.mood,
      position: profile?.position,
    };

    const me = await apiFactory.userApi.saveMe(params);

    if (me?.status === 200) {
      updateUser(me?.data?.user);
      updateLanguageMap(me?.data?.languageMap);
      updateStickerKeywords(me?.data?.stickerKeywords);
    } else {
      openNotificationError(me?.message);
      updateUser(null);
    }
  };

  const removeAvatar = async () => {
    setIsLoading(true);
    const result = await apiFactory.userApi.remove();

    if (result?.status === 200) {
      updateUser({ ...user, avatar: null });
      openNotificationSuccess(result?.message);
    } else {
      openNotificationError(result?.message);
    }

    setIsLoading(false);
  };

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      openNotificationError(
        languageMap?.["error.onlyUpImg"] ?? "You can only upload image!",
      );
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const handleUpload = async ({ file, onProgress, onSuccess, onError }) => {
    try {
      setProgress(0);
      setStatus("UPLOADING");

      const result = await uploadMultipartFile({
        file,
        bucket: "avatar",
        onProgress: ({ percent }) => {
          setProgress(percent);

          onProgress?.({
            percent,
          });
        },
      });

      setProgress(100);
      setStatus("COMPLETED");

      const res = await apiFactory.userApi.upload({
        avatar: result?.location,
      });

      if (res?.status === 200) {
        openNotificationSuccess(
          languageMap?.["success.avtUpload"] ?? "Avatar uploaded successfully.",
        );
        user.avatar = result?.location;
        updateUser({ ...user });
        onSuccess?.(result);
      }
    } catch (error) {
      setStatus("FAILED");
      onError?.(error);

      openNotificationError("Upload failed!");
    }
  };

  return (
    <Modal
      title={languageMap?.["menu.profile.myProfile"] ?? "My Profile"}
      open={isModalOpen}
      onCancel={closeModal}
      footer={[]}
    >
      <div className="relative flex flex-col gap-[10px]">
        <div className="flex items-center gap-[20px]">
          <div className="w-[100px] font-semibold">
            {languageMap?.["menu.profile.avatar"] ?? "Avatar"}:
          </div>
          <div>
            <div className="relative inline-block w-20 h-20 cursor-pointer">
              <ProfileAvatar />
              <ImgCrop rotationSlider>
                <Upload
                  showUploadList={false}
                  beforeUpload={beforeUpload}
                  customRequest={handleUpload}
                >
                  <button
                    type="button"
                    className="hover:!bg-[#d9d9d9] absolute bottom-0 right-0 bg-white rounded-full p-1 shadow-md cursor-pointer"
                  >
                    <CiCamera className="text-xl" />
                  </button>
                </Upload>
              </ImgCrop>
            </div>
          </div>
          {user?.avatar && (
            <button onClick={() => setIsRemoveAvatar(true)}>
              <MdDelete size={20} color="#ef4444" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-[20px]">
          <div className="w-[100px] font-semibold">
            {languageMap?.["menu.profile.id"] ?? "ID"}:
          </div>
          <div>{user?.userId} </div>
        </div>
        <div className="flex items-center gap-[20px]">
          <div className="w-[100px] font-semibold">
            {languageMap?.["menu.profile.name"] ?? "Name"}:
          </div>
          <div>{user?.name} </div>
        </div>
        <div className="flex items-center gap-[20px]">
          <div className="w-[100px] font-semibold">
            {languageMap?.["menu.profile.email"] ?? "Email"}:
          </div>
          <div>{user?.email}</div>
        </div>

        <div className="flex items-center gap-[20px]">
          <div className="w-[100px] font-semibold">
            {languageMap?.["menu.profile.position"] ?? "Position"}:
          </div>
          <Input
            className="w-[300px]"
            value={profile?.position}
            disabled={!isEditPosition}
            onChange={(e) => onChangeProfile(e, "position")}
            maxLength={50}
          />
          {isEditPosition ? (
            <button onClick={() => onStoreProfile("position")}>
              <HiSave size={20} color="#2a56b9" />
            </button>
          ) : (
            <button onClick={() => setIsEditPosition(true)}>
              <AiFillEdit size={20} color="#333333" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-[20px]">
          <div className="w-[100px] font-semibold">
            {languageMap?.["menu.profile.tel"] ?? "Tel"}:
          </div>
          <Input
            className="w-[300px]"
            value={profile?.phone}
            disabled={!isEditPhone}
            maxLength={17}
            onChange={(e) => onChangeProfile(e, "phone")}
            onKeyPress={(event) => {
              if (!/[0-9.+-]/.test(event.key)) {
                event.preventDefault();
              }
            }}
          />
          {isEditPhone ? (
            <button onClick={() => onStoreProfile("phone")}>
              <HiSave size={20} color="#2a56b9" />
            </button>
          ) : (
            <button onClick={() => setIsEditPhone(true)}>
              <AiFillEdit size={20} color="#333333" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-[20px]">
          <div className="w-[100px] font-semibold">
            {languageMap?.["menu.profile.status"] ?? "Status"}:
          </div>
          <Input
            className="w-[300px]"
            value={profile?.mood}
            disabled={!isEditMood}
            onChange={(e) => onChangeProfile(e, "mood")}
          />
          {isEditMood ? (
            <button onClick={() => onStoreProfile("mood")}>
              <HiSave size={20} color="#2a56b9" />
            </button>
          ) : (
            <button onClick={() => setIsEditMood(true)}>
              <AiFillEdit size={20} color="#333333" />
            </button>
          )}
        </div>
        {isLoading && (
          <div className="absolute top-[30%] w-full flex justify-center">
            <Spin
              indicator={<LoadingOutlined className="text-[35px]" spin />}
            />
          </div>
        )}
      </div>
      {isRemoveAvatar && (
        <GeneralModal
          title={"Remove Avatar"}
          content={"Do you want to delete avatar?"}
          onCancel={() => setIsRemoveAvatar(false)}
          open={() => setIsRemoveAvatar(true)}
          onConfirm={removeAvatar}
        />
      )}
    </Modal>
  );
};

export { ProfileModal };
